export type AnalyticsConsent = 'accepted' | 'rejected'

/**
 * What PostHog should be told, given the visitor's stored decision and whether their region
 * has been resolved yet.
 *
 * Extracted from the provider deliberately: this is the highest-consequence branch in the
 * analytics stack — the wrong answer either loses every event or starts writing cookies before
 * the visitor consented — and it is three-valued in a way that is easy to get wrong twice.
 *
 * - `opt_in`  → persistent, identified capture + session replay.
 * - `opt_out` → NOT "stop capturing". Under `cookieless_mode: 'on_reject'` this is the
 *   PENDING→DENIED transition that *engages* cookieless capture. `is_capturing()` is
 *   `isExplicitlyOptedOut() || isOptedIn()`, so a merely-pending visitor is captured not at all.
 * - `wait`    → do nothing yet. Only for an undecided visitor whose region is still unknown:
 *   outside the EU/EEA/UK `consent === null` is transient (it resolves to 'accepted' moments
 *   later), and opting out during that window would emit the landing $pageview under the
 *   cookieless sentinel id and let the follow-up opt-in wipe $initial_utm_* / $initial_referrer.
 */
export function resolveCaptureMode(
  consent: AnalyticsConsent | null,
  requiresConsent: boolean | null
): 'opt_in' | 'opt_out' | 'wait' {
  if (consent === 'accepted') return 'opt_in'
  if (consent === 'rejected') return 'opt_out'
  // Undecided: cookieless only once we know the visitor is in an opt-in region.
  return requiresConsent === true ? 'opt_out' : 'wait'
}

export const ANALYTICS_CONSENT_KEY = 'ranksurf:analytics-consent'
export const CONSENT_CHANGE_EVENT = 'ranksurf:open-cookie-preferences'

export function getStoredConsent(): AnalyticsConsent | null {
  if (typeof window === 'undefined') return null
  try {
    const value = localStorage.getItem(ANALYTICS_CONSENT_KEY)
    if (value === 'accepted' || value === 'rejected') return value
    return null
  } catch {
    return null
  }
}

/**
 * How the banner choice, which lives only in this browser's `localStorage`, reaches a server route
 * (R6.17 AC-6, planner decision P7): `apiCall` adds this header to same-origin calls. Nothing new is
 * stored on the device, so `/privacy` is untouched. A server event is tied to the account only when
 * the header is exactly `accepted`.
 */
export const ANALYTICS_CONSENT_HEADER = 'x-ranksurf-analytics-consent'

/** What the browser sends: the stored choice, or `pending` while the banner is unanswered. */
export function analyticsConsentHeaderValue(): AnalyticsConsent | 'pending' {
  return getStoredConsent() ?? 'pending'
}

/**
 * What a server route makes of the header. Fails closed on privacy: exactly `accepted` is accepted;
 * a missing, mixed-case, `granted`, repeated (`accepted, accepted`) or forged value is not.
 */
export function serverAnalyticsConsent(headers: Headers): 'accepted' | 'not_accepted' {
  return headers.get(ANALYTICS_CONSENT_HEADER) === 'accepted' ? 'accepted' : 'not_accepted'
}

/** The slice of the PostHog client the identity reset touches. */
interface ResettableAnalyticsClient {
  reset: () => void
  opt_in_capturing: (options?: { captureEventName?: null | false }) => void
}

/**
 * The identity hygiene on cleanup (`posthog.reset()`), without losing the visitor's consent.
 *
 * `reset()` also clears PostHog's stored opt-in key, and the consent provider opts in only once on a
 * stored `accepted` — so after a reset the visitor read `pending` for the life of the page and, under
 * `cookieless_mode: 'on_reject'`, nothing was captured (R6.17 AC-4: no `suggestion_viewed`/`_opened`).
 * Restore the opt-in only for a stored `accepted`, and silently (`captureEventName: false`) so no
 * second `$opt_in` event is emitted. `rejected` or no choice never opts in: this fails closed.
 */
export function resetIdentityKeepingConsent(
  client: ResettableAnalyticsClient,
  storedConsent: AnalyticsConsent | null
): void {
  client.reset()
  if (storedConsent === 'accepted') client.opt_in_capturing({ captureEventName: false })
}

export function setStoredConsent(value: AnalyticsConsent): void {
  try {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, value)
  } catch {
    // ignore
  }
}

/**
 * The region auto-accept (outside the EU/EEA/UK an undecided visitor is accepted without a banner).
 * It re-reads storage at the moment of the write: the provider's effect runs on the hydration commit
 * with `consent` still `null`, so its closure can be stale, and a stored choice — `rejected` above all —
 * must never be replaced. Returns whether it wrote.
 */
export function autoAcceptIfUndecided(): boolean {
  if (getStoredConsent() !== null) return false
  setStoredConsent('accepted')
  return true
}

// Caches whether the visitor's region requires opt-in consent (EU/EEA/UK), so we
// only hit /api/geo once per browser while the visitor is still undecided.
export const REQUIRES_CONSENT_CACHE_KEY = 'ranksurf:requires-consent'

export function getCachedRequiresConsent(): boolean | null {
  if (typeof window === 'undefined') return null
  try {
    const value = localStorage.getItem(REQUIRES_CONSENT_CACHE_KEY)
    if (value === 'true') return true
    if (value === 'false') return false
    return null
  } catch {
    return null
  }
}

export function setCachedRequiresConsent(value: boolean): void {
  try {
    localStorage.setItem(REQUIRES_CONSENT_CACHE_KEY, String(value))
  } catch {
    // ignore
  }
}
